import { useCallback, useEffect, useRef, useState } from 'react';

import { useBizyExternalOpenStore } from '../store/use-bizy-external-open-store';

interface UseBizyExternalOpenAutoSendOptions {
	isPanelOpen: boolean;
	isEmptyState: boolean;
	/** Send wrapper that also owns scrolling to the new turn (and any blank-slate dismissal). */
	sendMessage: (message: string) => void;
	resetChat: () => void;
	loadConversation: (conversationId: string) => void;
	currentConversationId: string | undefined;
	/** True while a conversation load is in flight (Boolean(loadConversationId)). */
	isLoadingConversation: boolean;
	/** Drops text into the chat input without sending (prefill deep links). */
	setInputValue: (text: string) => void;
}

/**
 * Consumes a pending external-open request (feature-request button, URL deep
 * link) once the panel is open. Intent is expressed via the pending request
 * fields:
 *
 * - `conversationId` set: loads that conversation first, then (if a starter is
 *   present) sends/prefills INTO it — the deep link explicitly targeted it.
 * - `forceNewConversation`: resets any active conversation, then sends/prefills
 *   the starter into a fresh one. Used by URL deep links where the URL already
 *   expresses the intent (a bare `?bizy_prompt=` is a new topic) — no dialog.
 * - `autoSend === false`: drops the text into the input for the user to
 *   review/edit instead of sending.
 * - Otherwise (feature-request button): empty chat sends immediately; an active
 *   conversation shows a confirmation dialog (keep vs start new).
 *
 * Returns the current pendingRequest plus dialog state for the chat view.
 */
export function useBizyExternalOpenAutoSend({
	isPanelOpen,
	isEmptyState,
	sendMessage,
	resetChat,
	loadConversation,
	currentConversationId,
	isLoadingConversation,
	setInputValue,
}: UseBizyExternalOpenAutoSendOptions) {
	const pendingRequest = useBizyExternalOpenStore((state) => state.pendingRequest);
	const clearPendingRequest = useBizyExternalOpenStore((state) => state.clearPendingRequest);
	const handledRef = useRef(false);
	const loadRequestedRef = useRef(false);
	const [showConflictDialog, setShowConflictDialog] = useState(false);
	// Starter queued to apply once resetChat has flushed and the chat is empty.
	const [pendingApply, setPendingApply] = useState<{ message: string; autoSend: boolean } | null>(null);

	// Apply the starter once we're on the intended chat: send it, or
	// (prefill mode) drop it into the input for the user to review.
	const applyStarter = useCallback(
		(message: string, autoSend: boolean) => {
			if (autoSend) {
				sendMessage(message);
			} else {
				setInputValue(message);
			}
		},
		[sendMessage, setInputValue]
	);

	useEffect(() => {
		if (!isPanelOpen || !pendingRequest || handledRef.current) return;

		const { conversationId, starterMessage, autoSend = true, forceNewConversation } = pendingRequest;

		// Conversation deep link: load the target conversation before applying
		// any starter, so the prompt lands as a follow-up inside it.
		if (conversationId) {
			if (currentConversationId !== conversationId) {
				if (!loadRequestedRef.current) {
					loadRequestedRef.current = true;
					loadConversation(conversationId);
					return;
				}
				// Still loading — wait for it to land.
				if (isLoadingConversation) return;
				// Load settled but we're not on the target conversation: it
				// failed (network, 404, unauthorized). Give up rather than hang.
				handledRef.current = true;
				clearPendingRequest();
				return;
			}

			handledRef.current = true;
			if (starterMessage) applyStarter(starterMessage, autoSend);
			clearPendingRequest();
			return;
		}

		if (!starterMessage) return;

		// Deep-link starter into an active conversation: the URL already says
		// "new topic", so reset first and apply once the chat is empty. No
		// dialog. (When already empty, fall through to apply directly below.)
		if (forceNewConversation && !isEmptyState) {
			handledRef.current = true;
			resetChat();
			setPendingApply({ message: starterMessage, autoSend });
			return;
		}

		// Prefill mode: drop into the input, no send, no conflict dialog.
		if (!autoSend) {
			handledRef.current = true;
			setInputValue(starterMessage);
			clearPendingRequest();
			return;
		}

		if (isEmptyState) {
			handledRef.current = true;
			sendMessage(starterMessage);
			clearPendingRequest();
		} else {
			// Feature-request button into an active conversation: ask the user.
			setShowConflictDialog(true);
		}
	}, [
		isPanelOpen,
		isEmptyState,
		pendingRequest,
		currentConversationId,
		isLoadingConversation,
		applyStarter,
		sendMessage,
		setInputValue,
		resetChat,
		loadConversation,
		clearPendingRequest,
	]);

	useEffect(() => {
		if (!pendingRequest) {
			handledRef.current = false;
			loadRequestedRef.current = false;
		}
	}, [pendingRequest]);

	// Apply the deferred starter once resetChat has flushed and the chat is empty
	useEffect(() => {
		if (pendingApply && isEmptyState) {
			applyStarter(pendingApply.message, pendingApply.autoSend);
			clearPendingRequest();
			setPendingApply(null);
		}
	}, [pendingApply, isEmptyState, applyStarter, clearPendingRequest]);

	const handleConfirmNewConversation = useCallback(() => {
		const message = pendingRequest?.starterMessage;
		if (!message) return;
		resetChat();
		setShowConflictDialog(false);
		handledRef.current = true;
		setPendingApply({ message, autoSend: true });
	}, [pendingRequest, resetChat]);

	const handleKeepCurrentConversation = useCallback(() => {
		setShowConflictDialog(false);
		clearPendingRequest();
	}, [clearPendingRequest]);

	return {
		pendingRequest,
		showConflictDialog,
		handleConfirmNewConversation,
		handleKeepCurrentConversation,
	};
}
